import { CpwCustomPermissions } from '../CustomPermissions';

export enum ReportingCategory {
    OverallKPIs = 'Overall KPIs',
    Exceptions = 'Exceptions',
    Inbound = 'Inbound',
    Outbound = 'Outbound',
    Inventory = 'Inventory',
    Warehouse = 'Warehouse',
    Auditing = 'Auditing',
    Workforce = 'Workforce',
    Suppliers = 'Suppliers',
}

export interface MetabaseDashboardConfig {
    slug: string;
    displayName: string;
    description: string;
    category: ReportingCategory;
    permission: CpwCustomPermissions;
}

export const CPW_REPORTING_DASHBOARDS: MetabaseDashboardConfig[] = [
    // Overall KPIs
    {
        slug: 'daily-operations-summary',
        displayName: 'Daily Operations Summary',
        description:
            "Snapshot of today's receiving, picking, packing, and shipping activity.",
        category: ReportingCategory.OverallKPIs,
        permission: CpwCustomPermissions.ReportingCanViewDailyOperationsSummary,
    },
    {
        slug: 'inventory-health',
        displayName: 'Inventory Health',
        description:
            'Stock levels, accuracy, and aging across all warehouse locations.',
        category: ReportingCategory.OverallKPIs,
        permission: CpwCustomPermissions.ReportingCanViewInventoryHealth,
    },
    {
        slug: 'warehouse-performance',
        displayName: 'Warehouse Performance',
        description: 'Throughput and utilization across warehouse operations.',
        category: ReportingCategory.OverallKPIs,
        permission: CpwCustomPermissions.ReportingCanViewWarehousePerformance,
    },

    // Exceptions
    {
        slug: 'exceptions-overview',
        displayName: 'Exceptions Overview',
        description: 'Open exceptions broken down by type, age, and severity.',
        category: ReportingCategory.Exceptions,
        permission: CpwCustomPermissions.ReportingCanViewExceptionsOverview,
    },
    {
        slug: 'exception-trends',
        displayName: 'Exception Trends',
        description:
            'Historical patterns and root causes of exception activity.',
        category: ReportingCategory.Exceptions,
        permission: CpwCustomPermissions.ReportingCanViewExceptionTrends,
    },

    // Inbound
    {
        slug: 'requisition-pipeline',
        displayName: 'Requisition Pipeline',
        description:
            'Pending and in-progress requisitions awaiting fulfillment.',
        category: ReportingCategory.Inbound,
        permission: CpwCustomPermissions.ReportingCanViewRequisitionPipeline,
    },
    {
        slug: 'purchase-orders',
        displayName: 'Purchase Orders',
        description:
            'PO status, lead times, and supplier delivery performance.',
        category: ReportingCategory.Inbound,
        permission: CpwCustomPermissions.ReportingCanViewPurchaseOrders,
    },
    {
        slug: 'asns',
        displayName: 'ASNs',
        description: 'Inbound ASN volume, accuracy, and on-time arrival.',
        category: ReportingCategory.Inbound,
        permission: CpwCustomPermissions.ReportingCanViewASNs,
    },
    {
        slug: 'receiving-productivity',
        displayName: 'Receiving Productivity',
        description:
            'Receiver throughput, dock-to-stock time, and put-away accuracy.',
        category: ReportingCategory.Inbound,
        permission: CpwCustomPermissions.ReportingCanViewReceivingProductivity,
    },

    // Outbound
    {
        slug: 'work-order',
        displayName: 'Work Order',
        description: 'Open and completed work order volume and cycle time.',
        category: ReportingCategory.Outbound,
        permission: CpwCustomPermissions.ReportingCanViewWorkOrder,
    },
    {
        slug: 'picking-performance',
        displayName: 'Picking Performance',
        description: 'Pick rate, accuracy, and picker productivity over time.',
        category: ReportingCategory.Outbound,
        permission: CpwCustomPermissions.ReportingCanViewPickingPerformance,
    },
    {
        slug: 'shipments',
        displayName: 'Shipments',
        description:
            'Outbound shipment volume, carrier mix, and on-time performance.',
        category: ReportingCategory.Outbound,
        permission: CpwCustomPermissions.ReportingCanViewShipments,
    },

    // Inventory
    {
        slug: 'inventory-snapshot',
        displayName: 'Inventory Snapshot',
        description:
            'Current on-hand quantities across SKUs and storage locations.',
        category: ReportingCategory.Inventory,
        permission: CpwCustomPermissions.ReportingCanViewInventorySnapshot,
    },
    {
        slug: 'inventory-aging',
        displayName: 'Inventory Aging',
        description:
            'Stock age distribution and slow-moving inventory indicators.',
        category: ReportingCategory.Inventory,
        permission: CpwCustomPermissions.ReportingCanViewInventoryAging,
    },
    {
        slug: 'item-master-catalog',
        displayName: 'Item Master Catalog',
        description: 'Active SKUs, attributes, and item-master completeness.',
        category: ReportingCategory.Inventory,
        permission: CpwCustomPermissions.ReportingCanViewItemMasterCatalog,
    },
    {
        slug: 'transaction-register',
        displayName: 'Transaction Register',
        description: 'Detailed log of every inventory movement and adjustment.',
        category: ReportingCategory.Inventory,
        permission: CpwCustomPermissions.ReportingCanViewTransactionRegister,
    },

    // Warehouse
    {
        slug: 'warehouse-locations',
        displayName: 'Warehouse Locations',
        description: 'Bin utilization, capacity, and slotting health.',
        category: ReportingCategory.Warehouse,
        permission: CpwCustomPermissions.ReportingCanViewWarehouseLocations,
    },
    {
        slug: 'equipment-tracking',
        displayName: 'Equipment Tracking',
        description:
            'Forklift, cart, and warehouse asset usage and availability.',
        category: ReportingCategory.Warehouse,
        permission: CpwCustomPermissions.ReportingCanViewEquipmentTracking,
    },
    {
        slug: 'container-tracking',
        displayName: 'Container Tracking',
        description: 'Container counts, current locations, and dwell time.',
        category: ReportingCategory.Warehouse,
        permission: CpwCustomPermissions.ReportingCanViewContainerTracking,
    },

    // Auditing
    {
        slug: 'cycle-counts',
        displayName: 'Cycle Counts',
        description:
            'Cycle count completion, variance, and inventory accuracy trends.',
        category: ReportingCategory.Auditing,
        permission: CpwCustomPermissions.ReportingCanViewCycleCounts,
    },
    {
        slug: 'replenishments',
        displayName: 'Replenishments',
        description: 'Replenishment volume, fill rate, and turnaround time.',
        category: ReportingCategory.Auditing,
        permission: CpwCustomPermissions.ReportingCanViewReplenishments,
    },

    // Workforce
    {
        slug: 'workforce',
        displayName: 'Workforce',
        description: 'Labor hours, headcount, and productivity by role.',
        category: ReportingCategory.Workforce,
        permission: CpwCustomPermissions.ReportingCanViewWorkforce,
    },

    // Suppliers
    {
        slug: 'supplier-scorecarding',
        displayName: 'Supplier Scorecarding',
        description:
            'Supplier quality, on-time delivery, and compliance metrics.',
        category: ReportingCategory.Suppliers,
        permission: CpwCustomPermissions.ReportingCanViewSupplierScorecarding,
    },
];

export function getDashboardBySlug(
    slug: string
): MetabaseDashboardConfig | undefined {
    return CPW_REPORTING_DASHBOARDS.find(d => d.slug === slug);
}

export function getDashboardsByCategory(
    category: ReportingCategory
): MetabaseDashboardConfig[] {
    return CPW_REPORTING_DASHBOARDS.filter(d => d.category === category);
}
